// ═══════════════════════════════════════════════════════════
// SearchView — Apple-inspired white theme, bilingual
// ═══════════════════════════════════════════════════════════

import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X } from "lucide-react";
import { doSearch, Department } from "@/lib/data";
import { useLang } from "@/lib/i18n";
import DepartmentCard from "./DepartmentCard";

type SearchMode = "all" | "dept" | "head" | "floor";

interface Props {
  query: string;
  mode: SearchMode;
  onQueryChange: (q: string) => void;
  onModeChange: (m: SearchMode) => void;
  onSelect: (d: Department) => void;
}

const EXAMPLES = ["34", "52", "نظم المعلومات", "Civil Service", "قانونية", "GF", "Auditorium", "08"];

export default function SearchView({ query, mode, onQueryChange, onModeChange, onSelect }: Props) {
  const { t, isRtl } = useLang();
  const inputRef = useRef<HTMLInputElement>(null);
  const results = query.trim() ? doSearch(query, mode) : [];

  const MODES: { key: SearchMode; label: string }[] = [
    { key: "all", label: t.searchAll },
    { key: "dept", label: t.searchDept },
    { key: "head", label: t.searchHead },
    { key: "floor", label: t.searchFloor },
  ];

  useEffect(() => {
    const tm = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(tm);
  }, []);

  return (
    <div className="min-h-screen flex flex-col pt-12 pb-4 px-4">
      {/* Header */}
      <div className="text-center mb-5">
        <h2 className="text-[18px] font-bold text-[#1A2747]">{t.searchTitle}</h2>
        <p className={`text-[9px] text-[#6B7280] mt-1 ${isRtl ? "" : "tracking-[1.5px] uppercase"}`}>
          {t.searchTitleEn}
        </p>
      </div>

      {/* Search input */}
      <div className="relative mb-3">
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={t.searchPlaceholderShort}
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          className={`w-full px-4 py-3.5 ${isRtl ? "pr-10 pl-10" : "pl-10 pr-10"} rounded-2xl text-[13px] text-[#1A2747]
            bg-white border border-[rgba(15,23,42,0.06)]
            shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_20px_rgba(15,23,42,0.04)]
            focus:border-[rgba(74,144,217,0.45)] focus:outline-none
            focus:shadow-[0_0_0_3px_rgba(74,144,217,0.10),0_8px_20px_rgba(15,23,42,0.05)]
            transition-all duration-200
            placeholder:text-[#9CA3AF]`}
          style={{ direction: isRtl ? "rtl" : "ltr" }}
        />
        <Search
          size={15}
          className={`absolute ${isRtl ? "left-3.5" : "right-3.5"} top-1/2 -translate-y-1/2 text-[#1A2747] opacity-60`}
        />
        <AnimatePresence>
          {query && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={() => onQueryChange("")}
              style={{ touchAction: "manipulation" }}
              className={`absolute ${isRtl ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-6 h-6 rounded-full
                bg-[rgba(15,23,42,0.06)] text-[#6B7280] hover:bg-[rgba(15,23,42,0.10)] hover:text-[#1A2747]
                flex items-center justify-center transition-colors`}
              aria-label="clear"
            >
              <X size={12} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Mode filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1" style={{ direction: isRtl ? "rtl" : "ltr" }}>
        {MODES.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => onModeChange(key)}
            style={{ touchAction: "manipulation" }}
            className={`px-3.5 py-1.5 rounded-full text-[11px] font-medium flex-shrink-0 border transition-all duration-200 ${
              mode === key
                ? "border-transparent bg-[#1A2747] text-white"
                : "border-[rgba(15,23,42,0.10)] bg-white text-[#6B7280] hover:border-[rgba(15,23,42,0.20)] hover:text-[#1A2747]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Results count */}
      {query && results.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-[10px] text-[#6B7280] mb-2 font-medium"
        >
          {t.searchResultCount(results.length)}
        </motion.div>
      )}

      {/* No results */}
      {query && results.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center justify-center py-12 text-center"
        >
          <div className="text-[32px] opacity-15 mb-3">🔍</div>
          <div className="text-[13px] text-[#1A2747] font-medium">{t.searchNoResults}</div>
          <div className="text-[10px] text-[#9CA3AF] mt-1">{t.searchNoResultsEn}</div>
        </motion.div>
      )}

      {/* Results list */}
      {results.length > 0 && (
        <div className="flex flex-col gap-2">
          {results.map((d, i) => (
            <DepartmentCard
              key={d.id}
              dept={d}
              index={i}
              highlight={query}
              onClick={() => onSelect(d)}
            />
          ))}
        </div>
      )}

      {/* Examples (when no query) */}
      {!query && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-4"
        >
          <div className="text-[10px] text-[#6B7280] mb-3 font-medium">{t.searchExamples}</div>
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                onClick={() => onQueryChange(ex)}
                style={{ touchAction: "manipulation" }}
                className="px-3 py-1.5 rounded-full text-[11px] text-[#1A2747] font-medium
                  bg-white border border-[rgba(15,23,42,0.08)]
                  hover:border-[rgba(74,144,217,0.35)] hover:text-[#4A90D9] transition-all duration-200"
              >
                {ex}
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
